import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Row, Col, Typography, message, AutoComplete, Spin, Select } from 'antd'; // Removed Card import, Removed DatePicker
import MomentDatePicker from './MomentDatePicker.tsx'; // Import the custom MomentDatePicker
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';

const { Option } = Select;
const { Text } = Typography;

const LabInputForm = ({ patientId, onSave, onCancel, pendingOrders = [] }) => {
  const [form] = Form.useForm();
  const [allComponentsData, setAllComponentsData] = useState([]);
  const [filteredLabOptions, setFilteredLabOptions] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [selectedComponentDetails, setSelectedComponentDetails] = useState({});
  const [labTemplates, setLabTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [orderedTemplateComponents, setOrderedTemplateComponents] = useState([]); // State for ordered components
  const [selectedPendingOrderId, setSelectedPendingOrderId] = useState(null);
  const [loadingPendingOrderDetails, setLoadingPendingOrderDetails] = useState(false);

  const fetchAllComponents = useCallback(async () => {
    setLoadingComponents(true);
    try {
      const response = await axios.get('/lab-templates/components/all');
      const components = response.data.data.components || [];
      setAllComponentsData(components);
      setFilteredLabOptions(components.map(comp => ({ value: comp.name, componentData: comp })));
      const bnpComponent = components.find(c => c.name === 'BNP');
      console.log('[DEBUG LabInputForm fetchAllComponents] BNP data from allComponentsData:', bnpComponent); // DEBUG LOG
    } catch (error) {
      console.error("Error fetching lab components:", error);
      message.error('Failed to load available lab components.');
      setAllComponentsData([]);
      setFilteredLabOptions([]);
    } finally {
      setLoadingComponents(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get('/lab-templates');
      setLabTemplates(response.data.data.templates || []);
    } catch (error) {
      console.error("Error fetching lab templates:", error);
      message.error('Failed to load lab templates for selection.');
      setLabTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    fetchAllComponents();
    fetchTemplates();
  }, [fetchAllComponents, fetchTemplates]);

  useEffect(() => {
    form.setFieldsValue({ resultDate: moment() });
  }, [form]);

  const handleSearch = (value) => {
    if (value) {
      const filtered = allComponentsData
        .filter(comp => comp.name.toUpperCase().includes(value.toUpperCase()))
        .map(comp => ({ value: comp.name, componentData: comp }));
      setFilteredLabOptions(filtered);
    } else {
      setFilteredLabOptions(allComponentsData.map(comp => ({ value: comp.name, componentData: comp })));
    }
  };

  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplateId(templateId);
    // Do not clear pending order when template is selected, allow both
    // setSelectedPendingOrderId(null); 
    setSelectedComponentDetails({});
    setOrderedTemplateComponents([]);


    if (!templateId) {
      // If template is cleared, but a pending order is selected, keep the pending order's components
      if (!selectedPendingOrderId) {
          form.setFieldsValue({ labComponents: [] });
      }
      return;
    }

    setLoadingTemplates(true); // Use loadingTemplates for template loading
    try {
      const response = await axios.get(`/lab-templates/${templateId}`);
      const template = response.data.data.template;
      if (template && template.components) {
        const initialValues = template.components.map(comp => ({
          testName: comp.name,
          units: comp.units,
          category: comp.category || 'Uncategorized',
          value: '', // Value should be empty for new entry from template
          referenceRangeLow: comp.referenceRangeLow,
          referenceRangeHigh: comp.referenceRangeHigh,
          referenceRangeText: comp.referenceRangeText,
          flag: '',
        }));
        form.setFieldsValue({ labComponents: initialValues });

        const detailsMap = {};
        template.components.forEach((comp, index) => {
            detailsMap[index] = comp;
        });
        setSelectedComponentDetails(detailsMap);
        // Set the ordered components state here
        setOrderedTemplateComponents(template.components);
      } else {
         // If template has no components, but a pending order is selected, keep the pending order's components
        if (!selectedPendingOrderId) {
            form.setFieldsValue({ labComponents: [] });
            setSelectedComponentDetails({});
            setOrderedTemplateComponents([]); // Clear order if no components
        }
      }
    } catch (error) {
      console.error("Error fetching selected lab template details:", error);
      message.error('Failed to load components for the selected template.');
       // If error loading template, but a pending order is selected, keep the pending order's components
       if (!selectedPendingOrderId) {
          form.setFieldsValue({ labComponents: [] });
          setSelectedComponentDetails({});
          setOrderedTemplateComponents([]); // Clear order on error
       }
    } finally {
      setLoadingTemplates(false);
    }
  };

  const onSelect = (value, option, fieldIndex) => {
    console.log('[DEBUG LabInputForm onSelect] Args:', { value, option, fieldIndex }); // DEBUG LOG
    const { componentData } = option;
    console.log('[DEBUG LabInputForm onSelect] componentData:', componentData); // DEBUG LOG
    if (componentData) {
      const currentLabComponents = form.getFieldValue('labComponents') || [];
      currentLabComponents[fieldIndex] = {
        ...currentLabComponents[fieldIndex],
        testName: componentData.name,
        units: componentData.units,
        category: componentData.category || 'Uncategorized', // Explicitly set category
        referenceRangeLow: componentData.referenceRangeLow,
        referenceRangeHigh: componentData.referenceRangeHigh,
        referenceRangeText: componentData.referenceRangeText,
      };
      try {
        form.setFieldsValue({ labComponents: currentLabComponents });
        console.log('[DEBUG LabInputForm onSelect] Form values after setFieldsValue for labComponents:', form.getFieldValue('labComponents')); // DEBUG LOG
      } catch (e) {
        console.error('[DEBUG LabInputForm onSelect] Error during setFieldsValue:', e); // DEBUG LOG
      }

      setSelectedComponentDetails(prev => ({
          ...prev,
          [fieldIndex]: componentData
      }));
      // console.log('[DEBUG LabInputForm onSelect] setSelectedComponentDetails call has been temporarily bypassed.'); // Re-enabled
    }
  };

  const handleFinish = async (values) => {
    console.log("LabInputForm submitted values:", values);
    const resultDate = values.resultDate ? values.resultDate.toISOString() : new Date().toISOString();

    const potentiallyValidComponents = (values.labComponents || []).filter(comp =>
        comp && (
            (comp.testName !== null && comp.testName !== undefined && comp.testName !== '') ||
            (comp.value !== null && comp.value !== undefined && comp.value !== '')
        )
    );

    const hasAtLeastOneValue = potentiallyValidComponents.some(comp =>
        comp.value !== null && comp.value !== undefined && comp.value !== ''
    );

    if (!hasAtLeastOneValue) {
        message.warning('Please enter a result value for at least one lab component.');
        return; 
    }

     const finalComponentsToSave = potentiallyValidComponents
        .filter(comp => comp.value !== null && comp.value !== undefined && comp.value !== '')
        .map(comp => {
            let refRangeString = comp.referenceRangeText || '';
            if ((comp.referenceRangeLow !== null && comp.referenceRangeLow !== undefined) && 
                (comp.referenceRangeHigh !== null && comp.referenceRangeHigh !== undefined)) {
                refRangeString = `${comp.referenceRangeLow} - ${comp.referenceRangeHigh}`;
            }
            return {
                ...comp,
                referenceRange: refRangeString, // Ensure this field is what backend expects
            };
        });


    const saveData = {
        resultDate,
        labComponents: finalComponentsToSave, 
        linkedOrderId: selectedPendingOrderId, 
    };

    onSave(saveData); 
  };


  const handlePendingOrderSelect = async (orderId) => {
    setSelectedPendingOrderId(orderId);
    // Do not clear template when pending order is selected
    // setSelectedTemplateId(null); 
    setSelectedComponentDetails({});
    setOrderedTemplateComponents([]);

    if (!orderId) {
       // If pending order is cleared, but a template is selected, keep the template's components
       if (!selectedTemplateId) {
           form.setFieldsValue({ labComponents: [] });
       }
      return;
    }

    const selectedOrder = pendingOrders.find(order => order._id === orderId);
    if (!selectedOrder) {
      message.error('Selected pending order not found.');
      form.setFieldsValue({ labComponents: [] });
      return;
    }

    setLoadingPendingOrderDetails(true);
    try {
      // If order.labTemplate is an ID or object with components, prioritize it
      let templateToLoad = null;
      if (selectedOrder.labTemplate) {
          // If labTemplate is just an ID, we need to fetch the template details
          if (typeof selectedOrder.labTemplate === 'string') {
              templateToLoad = selectedOrder.labTemplate;
          } 
          // If labTemplate is populated (object), use its components directly
          else if (typeof selectedOrder.labTemplate === 'object' && selectedOrder.labTemplate.components) {
              const initialValues = selectedOrder.labTemplate.components.map(comp => ({
                  testName: comp.name, units: comp.units, category: comp.category || 'Uncategorized', value: '', 
                  referenceRangeLow: comp.referenceRangeLow, referenceRangeHigh: comp.referenceRangeHigh, 
                  referenceRangeText: comp.referenceRangeText, flag: '',
              }));
              form.setFieldsValue({ labComponents: initialValues });
              const detailsMap = {};
              selectedOrder.labTemplate.components.forEach((comp, index) => { detailsMap[index] = comp; });
              setSelectedComponentDetails(detailsMap);
              setOrderedTemplateComponents(selectedOrder.labTemplate.components);
              setLoadingPendingOrderDetails(false);
              return; // Components loaded from populated template, exit early
          }
      }
      
      // If we need to fetch the template by ID
      if (templateToLoad) {
          await handleTemplateSelect(templateToLoad); // Reuse template loading logic
      } 
      // Fallback: If no template, check for orderDetails.labComponents
      else if (selectedOrder.orderDetails && selectedOrder.orderDetails.labComponents) {
        const initialValues = selectedOrder.orderDetails.labComponents.map(comp => ({
          testName: comp.name || comp.testName, units: comp.units, category: comp.category || 'Uncategorized', value: '',
          referenceRangeLow: comp.referenceRangeLow, referenceRangeHigh: comp.referenceRangeHigh, 
          referenceRangeText: comp.referenceRangeText, flag: '',
        }));
        form.setFieldsValue({ labComponents: initialValues });
        const detailsMap = {};
        selectedOrder.orderDetails.labComponents.forEach((comp, index) => { detailsMap[index] = comp; });
        setSelectedComponentDetails(detailsMap);
        setOrderedTemplateComponents(selectedOrder.orderDetails.labComponents);
      } 
      // Fallback: If no template or orderDetails components, check for single testName
      else if (selectedOrder.testName) {
            const componentDetail = allComponentsData.find(c => c.name === selectedOrder.testName);
            if (componentDetail) {
                const initialValues = [{
                    testName: componentDetail.name, units: componentDetail.units, category: componentDetail.category || 'Uncategorized', value: '',
                    referenceRangeLow: componentDetail.referenceRangeLow, referenceRangeHigh: componentDetail.referenceRangeHigh,
                    referenceRangeText: componentDetail.referenceRangeText, flag: '',
                }];
                form.setFieldsValue({ labComponents: initialValues });
                setSelectedComponentDetails({ 0: componentDetail });
                setOrderedTemplateComponents([componentDetail]);
            } else {
                 message.info(`Details for test "${selectedOrder.testName}" not found in master list. Please add manually or select a template.`);
                 form.setFieldsValue({ labComponents: [{ testName: selectedOrder.testName, value: '', units: '', category: 'Uncategorized', flag: '' }] });
                 setOrderedTemplateComponents([{ name: selectedOrder.testName, category: 'Uncategorized' }]);
            }
      } 
      // Final fallback: No components found
      else {
            message.info('Selected order does not have pre-defined components. Please add manually or select a template.');
            // Keep existing form values if a template was already selected
            if (!selectedTemplateId) {
                form.setFieldsValue({ labComponents: [] });
            }
      }
    } catch (error) {
      console.error("Error processing selected pending order:", error);
      message.error('Failed to load components for the selected pending order.');
       // Keep existing form values if a template was already selected
       if (!selectedTemplateId) {
           form.setFieldsValue({ labComponents: [] });
       }
    } finally {
      setLoadingPendingOrderDetails(false);
    }
  };


  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={{ labComponents: [] }}>
      <Form.Item name="resultDate" label="Result Date" rules={[{ required: true, message: 'Please select the result date!' }]}>
        <MomentDatePicker showTime style={{ width: '100%' }} />
      </Form.Item>

      {pendingOrders && pendingOrders.length > 0 && (
        <Form.Item label="Select Pending Lab Order (Optional)">
          <Select
            placeholder="Select a pending order to pre-fill"
            onChange={handlePendingOrderSelect}
            value={selectedPendingOrderId}
            allowClear
            loading={loadingPendingOrderDetails || loadingTemplates} // Show loading if either is active
            style={{ width: '100%' }}
            disabled={loadingPendingOrderDetails || loadingTemplates}
          >
            {pendingOrders.map(order => (
              <Option key={order._id} value={order._id}>
                {/* Prioritize populated template name, then orderDetails.testName, then fallback */}
                {order.labTemplate?.templateName || order.orderDetails?.testName || `Order ID: ${order._id.slice(-6)}`} (Ordered: {moment(order.orderDate).format('YYYY-MM-DD')})
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item label="Or Select Panel Template (Optional)">
        <Select
          placeholder="Select a panel to pre-fill components"
          onChange={handleTemplateSelect}
          value={selectedTemplateId}
          allowClear
          loading={loadingTemplates || loadingPendingOrderDetails}
          style={{ width: '100%' }}
          disabled={loadingTemplates || loadingPendingOrderDetails} // Removed !!selectedPendingOrderId
        >
          {labTemplates.map(template => (
            <Option key={template._id} value={template._id}>{template.templateName}</Option>
          ))}
        </Select>
      </Form.Item>

      <Typography.Text strong style={{ marginBottom: '8px', display: 'block' }}>Lab Components:</Typography.Text>
      <Spin spinning={loadingPendingOrderDetails || loadingTemplates}> {/* Spin wrapper for components list */}
      <Row gutter={8} style={{ marginBottom: '8px', padding: '8px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: '2px' }}>
        <Col span={6}><Text strong>Test Name</Text></Col>
        <Col span={4}><Text strong>Value</Text></Col>
        <Col span={4}><Text strong>Units</Text></Col>
        <Col span={6}><Text strong>Ref. Range</Text></Col>
        <Col span={3}><Text strong>Flag</Text></Col>
        <Col span={1}></Col>
      </Row>

      <Form.List name="labComponents">
        {(fields, { add, remove }) => {
          // Group fields by category
          const groupedFields = fields.reduce((acc, field) => {
            const category = form.getFieldValue(['labComponents', field.name, 'category']) || 'Uncategorized';
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push(field);
            return acc;
          }, {});

          // Determine category order more dynamically and robustly
          const currentFormCategories = new Set();
          fields.forEach(field => {
            currentFormCategories.add(form.getFieldValue(['labComponents', field.name, 'category']) || 'Uncategorized');
          });

          let categoryOrder = orderedTemplateComponents
            .map(comp => comp.category || 'Uncategorized')
            .filter((value, index, self) => self.indexOf(value) === index);

          currentFormCategories.forEach(cat => {
            if (!categoryOrder.includes(cat)) {
              categoryOrder.push(cat);
            }
          });
          
          // Ensure 'Uncategorized' is last if it exists and there are other categories,
          // but only if it's actually present in the current form's categories.
          if (categoryOrder.includes('Uncategorized') && currentFormCategories.has('Uncategorized') && categoryOrder.length > 1) {
              categoryOrder = categoryOrder.filter(cat => cat !== 'Uncategorized');
              categoryOrder.push('Uncategorized');
          }
          
          // If categoryOrder is empty but there are fields, populate it from current fields
          if (categoryOrder.length === 0 && fields.length > 0) {
            fields.forEach(field => {
                const cat = form.getFieldValue(['labComponents', field.name, 'category']) || 'Uncategorized';
                if(!categoryOrder.includes(cat)) {
                    categoryOrder.push(cat);
                }
            });
             // Small sort to make it somewhat predictable if multiple categories appear this way
            categoryOrder.sort();
            if (categoryOrder.includes('Uncategorized') && categoryOrder.length > 1) {
                categoryOrder = categoryOrder.filter(cat => cat !== 'Uncategorized');
                categoryOrder.push('Uncategorized');
            }
          }


          return (
            <>
              {categoryOrder.map(categoryValue => {
                const fieldsInCategory = groupedFields[categoryValue] || [];

                // Sort fields within the category based on their original order in orderedTemplateComponents
                const sortedFieldsInCategory = fieldsInCategory.sort((a, b) => {
                    const indexA = orderedTemplateComponents.findIndex(comp => comp.name === form.getFieldValue(['labComponents', a.name, 'testName']));
                    const indexB = orderedTemplateComponents.findIndex(comp => comp.name === form.getFieldValue(['labComponents', b.name, 'testName']));
                    return indexA - indexB;
                });


                return (
                  <div key={categoryValue} style={{ marginBottom: 24 }}>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                      {categoryValue}
                    </Typography.Title>
                    {/* Header Row for each category - This was duplicated, removing the one inside the map */}
                    {/* Map over fields within this category */}
                    {sortedFieldsInCategory.map(({ key, name, ...restField }) => {
                      // Determine if the current row is a custom one
                      const isCustomRow = !selectedComponentDetails[name] && !selectedPendingOrderId; // Custom if no template detail and no pending order selected
                      const currentUnits = form.getFieldValue(['labComponents', name, 'units']);
                      const currentRefRangeText = form.getFieldValue(['labComponents', name, 'referenceRangeText']);
                      const details = selectedComponentDetails[name];

                      return (
                        <div key={key} style={{ marginBottom: 8 }}>
                          <Row gutter={8} align="middle">
                            <Col span={6}>
                              <Form.Item
                                {...restField}
                                name={[name, 'testName']}
                                // rules={[{ required: true, message: 'Required' }]} // Removed required rule for testName
                                noStyle
                              >
                                <AutoComplete
                                  options={filteredLabOptions}
                                  onSearch={handleSearch}
                                  onSelect={(value, option) => onSelect(value, option, name)}
                                  placeholder="Test Name"
                                  style={{ width: '100%' }}
                                  loading={loadingComponents}
                                  // disabled={!!selectedPendingOrderId && !!details} // Keep Test Name editable even if pending order selected
                                />
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'value']}
                                // rules={[{ required: true, message: 'Required' }]} // Removed to allow submitting with at least one value
                                noStyle
                              >
                                <Input placeholder="Value" />
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              {isCustomRow ? (
                                <Form.Item {...restField} name={[name, 'units']} noStyle>
                                  <Input placeholder="Units" />
                                </Form.Item>
                              ) : (
                                <>
                                  <div style={{ paddingTop: '5px', height: '32px' }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      {details?.units || currentUnits || ''}
                                    </Text>
                                  </div>
                                  <Form.Item {...restField} name={[name, 'units']} noStyle hidden>
                                    <Input />
                                  </Form.Item>
                                </>
                              )}
                            </Col>
                            <Col span={6}>
                              {isCustomRow ? (
                                <Form.Item {...restField} name={[name, 'referenceRangeText']} noStyle>
                                  <Input placeholder="Ref. Range Text" />
                                </Form.Item>
                              ) : (
                                <div style={{ paddingTop: '5px', height: '32px' }}>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {/* Check if details exists before accessing properties */}
                                    {details ? 
                                      (details.referenceRangeText || 
                                        (details.referenceRangeLow !== null && details.referenceRangeHigh !== null ? 
                                          `${details.referenceRangeLow} - ${details.referenceRangeHigh}` : 
                                          'N/A')
                                      ) : 'N/A' // Fallback if details is undefined (custom row)
                                    }
                                  </Text>
                                </div>
                              )}
                            </Col>
                            <Col span={3}>
                              <Form.Item {...restField} name={[name, 'flag']} noStyle>
                                <Select
                                  placeholder="Flag"
                                  allowClear
                                  style={{ width: '100%' }}
                                  getPopupContainer={() => document.body} // Change to document.body
                                  dropdownStyle={{ minWidth: 120 }} // Add minWidth for dropdown
                                >
                                  <Option value="">Normal</Option>
                                  <Option value="L">L (Low)</Option>
                                  <Option value="H">H (High)</Option>
                                  <Option value="LL">LL (Crit Low)</Option>
                                  <Option value="HH">HH (Crit High)</Option>
                                  <Option value="A">A (Abnormal)</Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col span={1} style={{ textAlign: 'center' }}>
                              <DeleteOutlined
                                onClick={() => remove(name)}
                                style={{ color: 'red', cursor: 'pointer' }}
                                // hidden={!!selectedPendingOrderId && !!details} // Allow deleting rows even if from pending order/template
                              />
                            </Col>
                            <Form.Item {...restField} name={[name, 'category']} hidden noStyle>
                                <Input type="hidden" />
                            </Form.Item>
                          </Row>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {/* Always allow adding custom components */}
              <Form.Item style={{ marginTop: '16px' }}>
                <Button type="dashed" onClick={() => add({ testName: '', value: '', units: '', referenceRangeText: '', flag: '', category: 'Uncategorized' })} block icon={<PlusOutlined />}>
                  Add Custom Lab Component
                </Button>
              </Form.Item>
            </>
          );
        }}
      </Form.List>
      </Spin> {/* End Spin wrapper */}
      <Form.Item style={{ textAlign: 'right', marginTop: 24 }}>
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit">
          Save Lab Results
        </Button>
      </Form.Item>
    </Form>
  );
};

export default LabInputForm;
